import React from 'react';

function ResourceModal({
  resourceTag,
  resourceLoading,
  resourceSaving,
  allResources,
  selectedResources,
  activeResourceTab,
  setActiveResourceTab,
  toggleResourceSelection,
  getResourceTagBadge,
  openMultiTagModal,
  saveResourceAssignments,
  onClose,
}) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="p-6 border-b">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-lg font-semibold">
                管理資源 -
                <span
                  className="ml-2 px-3 py-1 rounded-full text-sm text-white font-medium"
                  style={{ backgroundColor: resourceTag.color || '#3B82F6' }}
                >
                  {resourceTag.name}
                </span>
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                選擇要指派給此權限標籤的專案和報表
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="border-b px-6">
          <div className="flex gap-4">
            <button
              onClick={() => setActiveResourceTab('projects')}
              className={`py-3 px-4 border-b-2 font-medium text-sm transition ${
                activeResourceTab === 'projects'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              專案 ({selectedResources.projects.length})
            </button>
            <button
              onClick={() => setActiveResourceTab('reports')}
              className={`py-3 px-4 border-b-2 font-medium text-sm transition ${
                activeResourceTab === 'reports'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              報表 ({selectedResources.reports.length})
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {resourceLoading ? (
            <div className="text-center py-8 text-gray-500">載入中...</div>
          ) : (
            <>
              {/* 專案列表 */}
              {activeResourceTab === 'projects' && (
                <div className="space-y-2">
                  {allResources.projects.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">沒有專案資料</div>
                  ) : (
                    allResources.projects.map((project) => (
                      <div
                        key={project.id}
                        className={`flex items-center justify-between p-3 rounded-lg border transition ${
                          selectedResources.projects.includes(project.id)
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <label className="flex items-center gap-3 cursor-pointer flex-1">
                          <input
                            type="checkbox"
                            checked={selectedResources.projects.includes(project.id)}
                            onChange={() => toggleResourceSelection('projects', project.id)}
                            className="w-4 h-4 text-blue-600 rounded"
                          />
                          <div>
                            <div className="font-medium">{project.name}</div>
                            <div className="text-sm text-gray-500">
                              狀態: {project.status || '未設定'}
                            </div>
                          </div>
                        </label>
                        <div className="flex items-center gap-2">
                          {getResourceTagBadge(project, resourceTag.name)}
                          <button
                            type="button"
                            onClick={() => openMultiTagModal('projects', project)}
                            className="ml-2 px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded hover:bg-purple-200 transition"
                          >
                            編輯標籤
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* 報表列表 */}
              {activeResourceTab === 'reports' && (
                <div className="space-y-2">
                  {allResources.reports.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">沒有報表資料</div>
                  ) : (
                    allResources.reports.map((report) => (
                      <div
                        key={report.id}
                        className={`flex items-center justify-between p-3 rounded-lg border transition ${
                          selectedResources.reports.includes(report.id)
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <label className="flex items-center gap-3 cursor-pointer flex-1">
                          <input
                            type="checkbox"
                            checked={selectedResources.reports.includes(report.id)}
                            onChange={() => toggleResourceSelection('reports', report.id)}
                            className="w-4 h-4 text-blue-600 rounded"
                          />
                          <div>
                            <div className="font-medium">{report.name}</div>
                            <div className="text-sm text-gray-500">
                              {report.year} 年 • {report.row_count || 0} 筆資料
                            </div>
                          </div>
                        </label>
                        <div className="flex items-center gap-2">
                          {getResourceTagBadge(report, resourceTag.name)}
                          <button
                            type="button"
                            onClick={() => openMultiTagModal('reports', report)}
                            className="ml-2 px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded hover:bg-purple-200 transition"
                          >
                            編輯標籤
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Modal Footer */}
        <div className="p-6 border-t bg-gray-50 flex justify-between items-center">
          <div className="text-sm text-gray-500">
            已選擇: {selectedResources.projects.length} 專案, {selectedResources.reports.length} 報表
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
            >
              取消
            </button>
            <button
              onClick={saveResourceAssignments}
              disabled={resourceSaving}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {resourceSaving ? '儲存中...' : '儲存變更'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ResourceModal;
